import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import {
  constants as appStorageConstants,
  records as appStorageRecords,
} from '@saucelabs/sl-module-app-storage';
import { Loader } from '@saucelabs/sl-component-loader';
import { Pagination } from '@saucelabs/ds-pagination';
import { modalActions } from '@saucelabs/sl-container-modal';
import { DataTable, DataTableColumn, TableActions } from '@saucelabs/ds-table';
import { NoData } from '@saucelabs/ds-data-states';
import {
  IconManualFill,
  IconSearchFill,
  IconSettingsFill,
  IconTrashFill,
} from '@saucelabs/ds-icons';

import globalConstants from '../../../constants';
import constants from '../constants';
import selectors from '../selectors';
import actions, { FetchGroupsArguments } from '../actions';
import NoAppsUploaded from './NoAppsUploaded';
import Search from './Search';
import Filters from './Filters';
import TableCellName from './TableCellName';
import TableCellOs from './TableCellOs';
import TableCellVersion from './TableCellVersion';
import TableCellBuild from './TableCellBuild';
import TableCellUploaded from './TableCellUploaded';
import DeleteModal from './DeleteModal';

const { FILTER_OS_VALUE_ALL } = constants;
const { APP_STORAGE_LIFETIME_DAYS } = appStorageConstants;

type RowData = {
  name: string;
  os: string;
  identifier: string;
  id: number; // Group ID
  version: string;
  build: string;
  uploaded: Date;
  group: appStorageRecords.AppStorageGroup;
};

type TableProps = {
  uploadBox?: React.ReactNode;
};

const Table:React.FC<TableProps> = ({ uploadBox }) => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const [
    groupSelectedForDeletion,
    setGroupSelectedForDeletion,
  ] = useState<appStorageRecords.AppStorageGroup|null>(null);

  const {
    currentPage,
    isLoading,
    groups,
    itemsPerPage,
    totalItems,
  } = useSelector(selectors.groupsSelector);
  const currentSearchQuery = useSelector(selectors.searchQuerySelector);
  const currentOsFilter = useSelector(selectors.filterOsSelector);

  const columns: DataTableColumn<RowData>[] = useMemo(() => (
    [
      {
        allowsSorting: true,
        customBodyRender: ((value, key, row) => <TableCellName group={row.group} />),
        field: 'name',
        key: 'name',
        name: 'Name',
      },
      {
        allowsSorting: true,
        customBodyRender: ((value, key, row) => <TableCellOs group={row.group} />),
        field: 'os',
        key: 'os',
        name: 'OS',
      },
      {
        allowsSorting: false, // alphabetical sorting isn't helpful here
        customBodyRender: ((value, key, row) => <TableCellVersion group={row.group} />),
        field: 'version',
        helpText: 'The value of "CFBundleShortVersionString" in iOS or "versionName" in Android',
        key: 'version',
        name: 'Version',
      },
      {
        allowsSorting: false, // alphabetical sorting isn't helpful here
        name: 'Build',
        field: 'build',
        key: 'build',
        customBodyRender: ((value, key, row) => <TableCellBuild group={row.group} />),
        helpText: 'The value of "CFBundleVersion" in iOS or "versionCode" in Android',
      },
      {
        allowsSorting: true,
        customBodyRender: ((value: string) => <div className="max-w-34.75 break-all">{value}</div>),
        field: 'identifier',
        key: 'identifier',
        name: 'Identifier',
      },
      {
        allowsSorting: true,
        field: 'id',
        key: 'id',
        name: 'Group ID',
      },
      {
        allowsSorting: true,
        customBodyRender: ((value, key, row) => <TableCellUploaded group={row.group} />),
        field: 'uploaded',
        helpText: `The last time a build was uploaded for this app. Apps will expire when no build has been uploaded for ${APP_STORAGE_LIFETIME_DAYS} days.`,
        key: 'uploaded',
        name: 'Uploaded',
      },
      {
        allowsSorting: false,
        customBodyRender: (value, key, row) => (
          <div className="w-14">
            <TableActions actions={[
              {
                label: 'Start Test',
                onPress: () => {
                  dispatch(actions.setCurrentGroup(row.group));
                  dispatch(push(`/live/app-testing/file/${row.group.recent.id}`));
                },
                icon: IconManualFill,
                primaryAction: true,
              },
              {
                label: 'Settings and App Versions',
                onPress: () => {
                  dispatch(actions.setCurrentGroup(row.group));
                  dispatch(push(`/app-management/groups/${row.group.id}`));
                },
                icon: IconSettingsFill,
                primaryAction: true,
              },
              {
                label: 'Delete',
                onPress: () => {
                  setGroupSelectedForDeletion(row.group);
                  void dispatch(
                    modalActions.showModal(constants.DELETE_GROUP_MODAL_IDENTIFIER),
                  );
                },
                icon: IconTrashFill,
                primaryAction: true,
              },
            ]}
            />
          </div>
        ),
        field: 'id',
        key: 'actions',
        name: '',
      },
    ]
  ), [dispatch]);

  const fetchGroupsWithFilters = useCallback((fetchGroupsArguments: FetchGroupsArguments = {}) => {
    void dispatch(actions.fetchGroups(fetchGroupsArguments));
  }, [dispatch]);

  useEffect(fetchGroupsWithFilters, [fetchGroupsWithFilters]);

  const rows = useMemo(
    () => (
      groups.map((group) => {
        const recentFile = group?.recent;
        return {
          name: recentFile?.getDisplayName(),
          os: recentFile?.kind,
          identifier: group?.name,
          id: group?.id,
          version: group?.getMostRecentUploadVersion(),
          build: group?.getMostRecentUploadBuild(),
          uploaded: recentFile?.getCreationDate(),
          group,
        };
      })),
    [groups],
  );

  const showUpdateLoader = isLoading && groups.length > 0;

  if (
    !groups.length && !isLoading &&
    !currentSearchQuery && currentOsFilter === FILTER_OS_VALUE_ALL
  ) {
    return <NoAppsUploaded uploadBox={uploadBox} />;
  }

  return (
    <>
      {uploadBox}
      <div className="h-full p-2 pt-0">
        <DeleteModal group={groupSelectedForDeletion} />
        <div className="relative">
          {showUpdateLoader && (
            <div className="absolute w-full h-screen z-10 flex items-center justify-center bg-opacity-80 bg-white">
              <Loader show />
            </div>
          )}
          <div className={showUpdateLoader ? 'h-screen overflow-hidden' : ''}>
            <div className="w-full flex flex-row flex-wrap gap-x-2 pt-2" >
              <div className="w-48 max-w-full mb-2">
                <Search />
              </div>
              <div className="mb-2">
                <Filters />
              </div>
            </div>
            <Loader
              className="my-2 mx-auto"
              show={isLoading && !groups.length}
            >
              {groups.length > 0 ? (
                <>
                  <div className="overflow-auto">
                    <div className="min-w-128">
                      <DataTable
                        aria-label="App Management Groups Table"
                        columns={columns}
                        data-qa="app-management-groups-table"
                        isPaginated={false} // DataTable's pagination is currently insufficient for our needs
                        rows={rows}
                      />
                    </div>
                  </div>
                  {(totalItems > globalConstants.TABLE_PAGE_SIZES[0]) && (
                    <Pagination
                      currentPage={currentPage}
                      data-qa="app-management-groups-table-pagination"
                      itemsPerPage={itemsPerPage}
                      itemsPerPageOptions={[...globalConstants.TABLE_PAGE_SIZES]}
                      onItemsPerPageChange={(newItemsPerPage) => (
                        fetchGroupsWithFilters({
                          entriesPerPage: newItemsPerPage,
                          paginationPage: 1,
                        })
                      )}
                      onPageChange={(newPageNumber) => (
                        fetchGroupsWithFilters({
                          paginationPage: newPageNumber,
                        })
                      )}
                      totalItems={totalItems}
                    />
                  )}
                </>
              ) : (
                <NoData
                  className="mx-auto"
                  data-qa="app-management-groups-table-no-files"
                  description="Try updating your search criteria."
                  icon={IconSearchFill}
                  spacing="large"
                  title="We found no results"
                />
              )}
            </Loader>
          </div>
        </div>
      </div>
    </>
  );
};

export default Table;
